/**
 * Transaction API wrappers (BUILD_SPEC.md §6.3).
 *
 * Thin adapters so React components don't import axios directly.
 * Mutation helpers do NOT invalidate React Query caches themselves —
 * that's the caller's job (page components own which keys to evict).
 */

import { api } from "./client";
import type { Action, Paginated, Transaction, TransactionStatus } from "./types";

export interface ListTxParams {
  status?: TransactionStatus;
  page?: number;
  limit?: number;
  search?: string;
}

export async function listTransactions(params: ListTxParams = {}): Promise<Paginated<Transaction>> {
  const res = await api.get<Paginated<Transaction>>("/transactions", { params });
  return res.data;
}

/** A transaction with its most recent three actions attached — the
 *  shape the dashboard cards consume. Returned by the list endpoint
 *  when `?include=actions` is set. */
export type TransactionWithActions = Transaction & { actions: Action[] };

export async function listTransactionsWithActions(
  params: Omit<ListTxParams, "page" | "limit"> & { page?: number; limit?: number } = {},
): Promise<Paginated<TransactionWithActions>> {
  const res = await api.get<Paginated<TransactionWithActions>>("/transactions", {
    params: { ...params, include: "actions" },
  });
  return res.data;
}

export async function getTransaction(id: string): Promise<{ transaction: Transaction; actions: Action[] }> {
  const res = await api.get<{ transaction: Transaction; actions: Action[] }>(`/transactions/${id}`);
  return res.data;
}

export interface CreateTxInput {
  requestNumber: string;
  year: number;
  region: string;
  serviceType: string;
  followUpPhone: string;
  notes?: string;
}

export async function createTransaction(input: CreateTxInput): Promise<Transaction> {
  const res = await api.post<{ transaction: Transaction }>("/transactions", input);
  return res.data.transaction;
}

export async function updateTransactionNotes(id: string, notes: string | null): Promise<Transaction> {
  const res = await api.put<{ transaction: Transaction }>(`/transactions/${id}`, { notes });
  return res.data.transaction;
}

/**
 * Lifecycle mutations. The API encodes archive/unarchive/pause/resume
 * as separate POST endpoints (§6.3) rather than a status-PATCH; we
 * keep that split here so the call sites read as the verb, not as
 * "set status to X".
 */
export async function archiveTransaction(id: string): Promise<Transaction> {
  const res = await api.post<{ transaction: Transaction }>(`/transactions/${id}/archive`);
  return res.data.transaction;
}
export async function unarchiveTransaction(id: string): Promise<Transaction> {
  const res = await api.post<{ transaction: Transaction }>(`/transactions/${id}/unarchive`);
  return res.data.transaction;
}
export async function pauseTransaction(id: string): Promise<Transaction> {
  const res = await api.post<{ transaction: Transaction }>(`/transactions/${id}/pause`);
  return res.data.transaction;
}
export async function resumeTransaction(id: string): Promise<Transaction> {
  const res = await api.post<{ transaction: Transaction }>(`/transactions/${id}/resume`);
  return res.data.transaction;
}

export async function deleteTransaction(id: string): Promise<void> {
  await api.delete(`/transactions/${id}`);
}
